/**
 * intakeService.js
 *
 * Intake & Admission workflow service for Field Notes EHR.
 * Handles: referrals, consents, intake forms, admission/discharge.
 *
 * Firestore Collections:
 *   - referrals/{referralId}
 *   - consents/{consentId}
 *   - sharedIndividuals/{individualId} (updated with intake data)
 *
 * Powered by Cold Harbor Digital
 */

import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, where, orderBy, onSnapshot, serverTimestamp, Timestamp,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { orgCollection, orgDoc } from "./orgPaths";
import { logAuditEvent } from "./auditService";

// ===== HELPERS =====

function getUser() {
  const u = auth.currentUser;
  if (!u) return null;
  return { uid: u.uid, email: u.email, displayName: u.displayName || u.email };
}

function getOrg() {
  return localStorage.getItem("fieldnotes_user_org") || "default-org";
}

// ─────────────────────────────────────────────────────────────────
// REFERRALS
// Pipeline: new → contacted → scheduled → admitted → closed
// ─────────────────────────────────────────────────────────────────

/**
 * Create a new referral
 */
export async function createReferral(referralData) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");
    const org = getOrg();

    const id = `ref_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const referral = {
      id,
      organization: org,
      status: "new",

      // Referral source
      sourceType: referralData.sourceType || "",       // self, provider, court, hospital, family, other
      sourceName: referralData.sourceName || "",
      sourcePhone: referralData.sourcePhone || "",
      sourceEmail: referralData.sourceEmail || "",
      sourceFax: referralData.sourceFax || "",

      // Individual info (pre-admission — may not have full record yet)
      firstName: referralData.firstName || "",
      lastName: referralData.lastName || "",
      dob: referralData.dob || "",
      phone: referralData.phone || "",
      email: referralData.email || "",
      address: referralData.address || "",

      // Referral details
      reason: referralData.reason || "",
      urgency: referralData.urgency || "routine",      // routine, urgent, emergent
      requestedProgram: referralData.requestedProgram || "",
      requestedServices: referralData.requestedServices || [],
      insuranceType: referralData.insuranceType || "",  // medicaid, medicare, private, uninsured
      insuranceId: referralData.insuranceId || "",
      notes: referralData.notes || "",

      // Tracking
      assignedTo: referralData.assignedTo || "",        // uid of intake coordinator
      assignedToName: referralData.assignedToName || "",
      individualId: "",                                  // linked after admission

      // Timestamps
      referralDate: referralData.referralDate || new Date().toISOString(),
      contactedDate: "",
      scheduledDate: "",
      admittedDate: "",
      closedDate: "",
      closedReason: "",

      createdBy: user.uid,
      createdByName: user.displayName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(orgDoc("referrals", id), referral);

    await logAuditEvent("referral.create", {
      referralId: id,
      individualName: `${referral.firstName} ${referral.lastName}`,
      source: referral.sourceType,
    });

    return { success: true, id, referral };
  } catch (error) {
    console.error("[Intake] Error creating referral:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Update referral status with audit trail
 */
export async function updateReferralStatus(referralId, newStatus, details = {}) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");

    const updates = {
      status: newStatus,
      updatedAt: serverTimestamp(),
    };

    // Set date fields based on status
    switch (newStatus) {
      case "contacted":
        updates.contactedDate = new Date().toISOString();
        break;
      case "scheduled":
        updates.scheduledDate = details.scheduledDate || new Date().toISOString();
        break;
      case "admitted":
        updates.admittedDate = new Date().toISOString();
        if (details.individualId) updates.individualId = details.individualId;
        break;
      case "closed":
        updates.closedDate = new Date().toISOString();
        updates.closedReason = details.closedReason || "";
        break;
      default:
        break;
    }

    // Merge any extra details
    if (details.assignedTo) updates.assignedTo = details.assignedTo;
    if (details.assignedToName) updates.assignedToName = details.assignedToName;
    if (details.notes) updates.notes = details.notes;

    await updateDoc(orgDoc("referrals", referralId), updates);

    await logAuditEvent("referral.status_change", {
      referralId,
      newStatus,
      ...details,
    });

    return { success: true };
  } catch (error) {
    console.error("[Intake] Error updating referral:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Update referral fields
 */
export async function updateReferral(referralId, updates) {
  try {
    await updateDoc(orgDoc("referrals", referralId), {
      ...updates,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error("[Intake] Error updating referral:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Real-time listener for org referrals
 */
export function onOrgReferrals(org, callback) {
  const q = query(
    orgCollection("referrals", org),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const referrals = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(referrals);
  }, (error) => {
    console.error("[Intake] Referrals listener error:", error);
    callback([]);
  });
}

/**
 * Get referrals by status
 */
export function onReferralsByStatus(org, status, callback) {
  const q = query(
    orgCollection("referrals", org),
    where("status", "==", status),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    const referrals = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(referrals);
  }, (error) => {
    console.error("[Intake] Referrals by status listener error:", error);
    callback([]);
  });
}

/**
 * Get a single referral
 */
export async function getReferral(referralId) {
  try {
    const snap = await getDoc(orgDoc("referrals", referralId));
    if (snap.exists()) return { id: snap.id, ...snap.data() };
    return null;
  } catch (error) {
    console.error("[Intake] Error getting referral:", error);
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// CONSENTS
// ─────────────────────────────────────────────────────────────────

// Standard consent types for behavioral health
export const CONSENT_TYPES = [
  {
    id: "treatment",
    label: "Consent to Treatment",
    description: "I consent to receive behavioral health treatment services including assessment, individual and/or group therapy, medication management, and case management as recommended by my treatment team.",
    required: true,
  },
  {
    id: "information_release",
    label: "Release of Information (ROI)",
    description: "I authorize the release and exchange of my protected health information between my treatment providers, insurance company, and other entities as specified for the purpose of treatment, payment, and healthcare operations.",
    required: true,
  },
  {
    id: "hipaa_notice",
    label: "HIPAA Notice of Privacy Practices",
    description: "I acknowledge that I have received and reviewed the Notice of Privacy Practices which describes how my health information may be used and disclosed, and my rights regarding my health information.",
    required: true,
  },
  {
    id: "telehealth",
    label: "Telehealth Consent",
    description: "I consent to receive services via telehealth/video conferencing when appropriate. I understand the potential risks and benefits of telehealth services.",
    required: false,
  },
  {
    id: "emergency_contact",
    label: "Emergency Contact Authorization",
    description: "I authorize my treatment team to contact the emergency contacts I have provided in the event of a clinical emergency or safety concern.",
    required: true,
  },
  {
    id: "medication",
    label: "Consent for Medication",
    description: "I consent to the prescription and administration of psychiatric medications as recommended by my prescriber. I understand the potential benefits, risks, and side effects.",
    required: false,
  },
  {
    id: "42cfr",
    label: "42 CFR Part 2 Consent (Substance Use)",
    description: "I consent to the disclosure of my substance use disorder treatment records as permitted under 42 CFR Part 2 for the purposes specified.",
    required: false,
  },
];

/**
 * Record consent (signed acknowledgment)
 */
export async function recordConsent(individualId, consentData) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");
    const org = getOrg();

    const id = `consent_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const consent = {
      id,
      organization: org,
      individualId,
      consentType: consentData.consentType,
      consentLabel: consentData.consentLabel || "",
      signedBy: consentData.signedBy || "",           // name of person signing
      signedByRelation: consentData.signedByRelation || "self", // self, guardian, parent, poa
      signedAt: serverTimestamp(),
      expiresAt: consentData.expiresAt || null,        // Timestamp or null for no expiry
      witnessedBy: user.displayName,
      witnessedByUid: user.uid,
      revoked: false,
      revokedAt: null,
      revokedReason: "",
      notes: consentData.notes || "",
      createdAt: serverTimestamp(),
    };

    await setDoc(orgDoc("consents", id), consent);

    await logAuditEvent("consent.recorded", {
      individualId,
      consentType: consentData.consentType,
      signedBy: consentData.signedBy,
    });

    return { success: true, id };
  } catch (error) {
    console.error("[Intake] Error recording consent:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Revoke a consent
 */
export async function revokeConsent(consentId, reason = "") {
  try {
    await updateDoc(orgDoc("consents", consentId), {
      revoked: true,
      revokedAt: serverTimestamp(),
      revokedReason: reason,
    });

    await logAuditEvent("consent.revoked", { consentId, reason });
    return { success: true };
  } catch (error) {
    console.error("[Intake] Error revoking consent:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Get all consents for an individual
 */
export function onIndividualConsents(individualId, callback) {
  const q = query(
    orgCollection("consents"),
    where("individualId", "==", individualId),
    orderBy("createdAt", "desc")
  );
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (error) => {
    console.error("[Intake] Consents listener error:", error);
    callback([]);
  });
}

// ─────────────────────────────────────────────────────────────────
// INTAKE / ADMISSION
// ─────────────────────────────────────────────────────────────────

// Insurance types
export const INSURANCE_TYPES = [
  { value: "medicaid", label: "Medicaid" },
  { value: "medicare", label: "Medicare" },
  { value: "medicaid_medicare", label: "Medicaid + Medicare (Dual)" },
  { value: "private", label: "Private Insurance" },
  { value: "self_pay", label: "Self-Pay" },
  { value: "sliding_scale", label: "Sliding Scale" },
  { value: "uninsured", label: "Uninsured" },
  { value: "other", label: "Other" },
];

// Programs
export const PROGRAMS = [
  { value: "ICM", label: "Intensive Case Management (ICM)" },
  { value: "PSR", label: "Psychosocial Rehabilitation (PSR)" },
  { value: "CST", label: "Community Support Team (CST)" },
  { value: "ACT", label: "Assertive Community Treatment (ACT)" },
  { value: "outpatient", label: "Outpatient Therapy" },
  { value: "med_management", label: "Medication Management" },
  { value: "crisis", label: "Crisis Stabilization" },
  { value: "peer_support", label: "Peer Support" },
  { value: "substance_use", label: "Substance Use Treatment" },
];

// Referral source types
export const REFERRAL_SOURCES = [
  { value: "self", label: "Self-Referral" },
  { value: "provider", label: "Provider / Physician" },
  { value: "hospital", label: "Hospital / ER" },
  { value: "court", label: "Court / Probation" },
  { value: "family", label: "Family Member" },
  { value: "school", label: "School" },
  { value: "dfcs", label: "DFCS / Child Welfare" },
  { value: "community", label: "Community Organization" },
  { value: "insurance", label: "Insurance / MCO" },
  { value: "transfer", label: "Transfer from Another Agency" },
  { value: "other", label: "Other" },
];

/**
 * Complete intake — creates a full individual record from intake form data.
 * This is the primary intake function that:
 *   1. Creates the sharedIndividual document
 *   2. Records all consents
 *   3. Links the referral (if from referral pipeline)
 *   4. Logs audit events
 *
 * Returns the new individualId
 */
export async function completeIntake(intakeData) {
  try {
    const user = getUser();
    if (!user) throw new Error("Not authenticated");
    const org = getOrg();

    const id = `ind_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    // Build the individual document
    const individual = {
      id,
      organization: org,

      // Demographics
      firstName: intakeData.firstName || "",
      lastName: intakeData.lastName || "",
      preferredName: intakeData.preferredName || "",
      dob: intakeData.dob || "",
      ssn: intakeData.ssn || "",             // stored encrypted in production
      gender: intakeData.gender || "",
      pronouns: intakeData.pronouns || "",
      race: intakeData.race || "",
      ethnicity: intakeData.ethnicity || "",
      preferredLanguage: intakeData.preferredLanguage || "English",
      maritalStatus: intakeData.maritalStatus || "",

      // Contact
      phone: intakeData.phone || "",
      email: intakeData.email || "",
      address: intakeData.address || "",
      city: intakeData.city || "",
      state: intakeData.state || "GA",
      zip: intakeData.zip || "",
      county: intakeData.county || "",

      // Emergency contacts
      emergencyContacts: intakeData.emergencyContacts || [],

      // Insurance
      insuranceType: intakeData.insuranceType || "",
      insurancePrimary: {
        payerName: intakeData.insurancePrimaryPayer || "",
        payerId: intakeData.insurancePrimaryPayerId || "",
        memberId: intakeData.insurancePrimaryMemberId || "",
        groupNumber: intakeData.insurancePrimaryGroup || "",
        subscriberName: intakeData.insurancePrimarySubscriber || "",
        subscriberRelation: intakeData.insurancePrimaryRelation || "self",
        effectiveDate: intakeData.insurancePrimaryEffective || "",
        expirationDate: intakeData.insurancePrimaryExpiration || "",
      },
      insuranceSecondary: intakeData.hasSecondaryInsurance ? {
        payerName: intakeData.insuranceSecondaryPayer || "",
        memberId: intakeData.insuranceSecondaryMemberId || "",
        groupNumber: intakeData.insuranceSecondaryGroup || "",
      } : null,

      // Clinical
      diagnoses: intakeData.diagnoses || [],
      medications: intakeData.medications || [],
      allergies: intakeData.allergies || [],
      riskLevel: intakeData.riskLevel || "low",
      presentingProblems: intakeData.presentingProblems || "",
      historyMentalHealth: intakeData.historyMentalHealth || "",
      historySubstanceUse: intakeData.historySubstanceUse || "",
      historyMedical: intakeData.historyMedical || "",
      historyTrauma: intakeData.historyTrauma || "",
      suicidalHistory: intakeData.suicidalHistory || "none", // none, past_ideation, past_attempt, current_ideation

      // Program & assignment
      program: intakeData.program || "ICM",
      admissionStatus: "admitted",
      admissionDate: new Date().toISOString(),
      referralId: intakeData.referralId || "",
      referralSource: intakeData.referralSource || "",

      // Provider assignment
      assignedAgent: intakeData.assignedAgent || user.uid,
      assignedAgentName: intakeData.assignedAgentName || user.displayName,
      assignedClinician: intakeData.assignedClinician || "",
      assignedClinicianName: intakeData.assignedClinicianName || "",
      assignedPrescriber: intakeData.assignedPrescriber || "",
      assignedPrescriberName: intakeData.assignedPrescriberName || "",

      // Legacy fields for backward compat
      assignedAgents: [intakeData.assignedAgent || user.uid],
      assignedAgentNames: [intakeData.assignedAgentName || user.displayName],
      primaryAgent: intakeData.assignedAgent || user.uid,
      primaryAgentName: intakeData.assignedAgentName || user.displayName,

      // Metadata
      createdBy: user.uid,
      createdByName: user.displayName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastContactDate: null,
      treatmentPlan: null,
    };

    // Save to Firestore
    await setDoc(orgDoc("individuals", id), individual);

    // Record consents
    if (intakeData.consents && intakeData.consents.length > 0) {
      for (const consent of intakeData.consents) {
        if (consent.signed) {
          await recordConsent(id, {
            consentType: consent.id,
            consentLabel: consent.label,
            signedBy: `${intakeData.firstName} ${intakeData.lastName}`,
            signedByRelation: intakeData.consentSignedByRelation || "self",
          });
        }
      }
    }

    // Link referral if applicable
    if (intakeData.referralId) {
      await updateReferralStatus(intakeData.referralId, "admitted", { individualId: id });
    }

    await logAuditEvent("intake.completed", {
      individualId: id,
      individualName: `${intakeData.firstName} ${intakeData.lastName}`,
      program: intakeData.program,
      referralId: intakeData.referralId || null,
    });

    return { success: true, id, individual };
  } catch (error) {
    console.error("[Intake] Error completing intake:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Update admission status on an existing individual
 */
export async function updateAdmissionStatus(individualId, status, details = {}) {
  try {
    const user = getUser();
    const updates = {
      admissionStatus: status,
      updatedAt: serverTimestamp(),
    };

    if (status === "discharged") {
      updates.dischargeDate = new Date().toISOString();
      updates.dischargeReason = details.reason || "";
      updates.dischargeSummary = details.summary || "";
      updates.aftercarePlan = details.aftercarePlan || "";
      updates.dischargedBy = user?.uid || "";
      updates.dischargedByName = user?.displayName || "";
    }

    await updateDoc(orgDoc("individuals", individualId), updates);

    await logAuditEvent(`admission.${status}`, {
      individualId,
      ...details,
    });

    return { success: true };
  } catch (error) {
    console.error("[Intake] Error updating admission:", error);
    return { success: false, error: error.message };
  }
}

/**
 * Get team members for assignment dropdowns
 */
export async function getTeamMembers(org) {
  try {
    const q = query(
      collection(db, "users"),
      where("organization", "==", org)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({
      id: d.id,
      ...d.data(),
      role: (d.data().role || "agent").trim(),
    }));
  } catch (error) {
    console.error("[Intake] Error loading team:", error);
    return [];
  }
}